import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { apiKeysApi, API_SCOPES } from '../../services/api'

export default function ApiKeyCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (rawToken: string) => void
}) {
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['messages:write'])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggle = (s: string) =>
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || scopes.length === 0) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiKeysApi.create({ name: name.trim(), scopes })
      onCreated(res.data.token)
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error?.toString?.() || 'Failed to create key')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-card rounded-xl shadow-2xl w-[440px] max-w-[95vw]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-foreground font-semibold">Create API key</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Name</span>
            <input
              autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alertmanager prod"
              className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            />
          </label>
          <div className="space-y-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Scopes</span>
            <div className="space-y-1.5">
              {API_SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} className="accent-primary" />
                  <code className="text-xs">{s}</code>
                </label>
              ))}
            </div>
          </div>
          {error && <div className="text-xs text-destructive bg-destructive/10 rounded px-3 py-2">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-accent">Cancel</button>
          <button type="submit" disabled={submitting || !name.trim() || scopes.length === 0} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50">
            {submitting && <Loader2 size={14} className="animate-spin" />}{submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  )
}
